import { GetStageResponseInfrastructureAsCodeToolEnum } from "@microtica/ms-engine-sdk";
import { ListKubernetesWithDetailsResponseKuberneteses, ListKubernetesWithDetailsResponseKubernetesesTypeEnum } from "@microtica/ms-kube-sdk";
import { Skeleton, Row, Col, Space, Card, Tooltip, Tag, Progress, Dropdown, Button, Alert, notification } from "antd";
import { DownOutlined, MoreOutlined, SearchOutlined, InfoCircleTwoTone, GlobalOutlined } from "@ant-design/icons";
import Title from "antd/lib/typography/Title";
import { useNavigate } from "react-router";
import { getKubeService } from "../backend";
import { trackKubeConnectInit } from "../backend/tracking/kubernetes";
import capitalizeFirstLetter from "../utils/capitalize-first-letter";
import KubernetesLogo from "../assets/kubernetes-icon.svg";
import { useDoubleConfigurationModal } from "../contexts/Modal";
import Text from "antd/lib/typography/Text";

interface StageClustersTableProps {
    clusters: ListKubernetesWithDetailsResponseKuberneteses[];
    environment: {
        id: string;
        infrastructureAsCodeTool?: GetStageResponseInfrastructureAsCodeToolEnum;
    };
    projectId: string;
    loading: boolean;
    setShowConnectClusterModal: (val: boolean) => void;
    fetchClusters: () => Promise<void>;
}

const StageClustersTable = ({
    clusters,
    environment,
    projectId,
    loading,
    setShowConnectClusterModal,
    fetchClusters
}: StageClustersTableProps) => {
    const navigate = useNavigate();
    const { open: openConfirmModal } = useDoubleConfigurationModal();

    const ClusterActions = () => (
        <Space>
            {/* {
                !projectHasSharedCluster &&
                <>
                    <Button
                        key="shared"
                        onClick={() => showRequestClusterConfirm(currentProject, envId, loadData)}
                    >
                        Request Free Demo Cluster
                    </Button>
                    or
                </>
            } */}
            <Dropdown
                trigger={["click"]}
                placement="bottomRight"
                menu={{
                    direction: "ltr",
                    items: [
                        {
                            key: "eks",
                            label: "Create Amazon EKS",
                            disabled: environment && environment.infrastructureAsCodeTool !== GetStageResponseInfrastructureAsCodeToolEnum.Cloudformation,
                            onClick: () => navigate("/templates/new?template=https://raw.githubusercontent.com/microtica/templates/master/aws-eks/template.yaml", { state: { envId: environment.id } })
                        },
                        {
                            key: "eks-spot",
                            label: "Create Amazon EKS Spot",
                            disabled: environment && environment.infrastructureAsCodeTool !== GetStageResponseInfrastructureAsCodeToolEnum.Cloudformation,
                            onClick: () => navigate("/templates/new?template=https://raw.githubusercontent.com/microtica/templates/master/aws-eks-spot/template.yaml", { state: { envId: environment.id } })
                        },
                        { type: "divider" },
                        {
                            key: "existing",
                            label: "Connect Existing Cluster",
                            onClick: () => {
                                setShowConnectClusterModal(true);
                                trackKubeConnectInit();
                            }
                        }
                    ]
                }}
            >
                <Button type={clusters.length ? "default" : "primary"} >
                    Create New Cluster <DownOutlined />
                </Button>
            </Dropdown>
        </Space>
    )

    const showDeleteClusterConfirm = (clusterId: string, clusterName: string) => {
        openConfirmModal({
            title: "Remove cluster",
            description:
                <Space direction='vertical'>
                    <div>This Kubernetes cluster will be removed from your account along with all the applications that are currently deployed.</div>
                    <Alert message={<b>This action cannot be undone.</b>} type="error" />
                </Space>,
            confirmation: clusterName,
            okText: "Remove",
            cancelText: "Cancel",
            onOk: () => handleRemoveCluster(clusterId, clusterName)
        });
    }

    const handleRemoveCluster = async (clusterId: string, clusterName: string) => {
        await getKubeService().deleteKubernetes(clusterId, projectId);
        notification.success({
            message: `Cluster removed`,
            description: `Cluster ${clusterName} was successfully removed`,
        });
        fetchClusters();
    }

    return (
        <Space direction="vertical" size="large" className="full-width">
            {
                loading ?
                    <Skeleton /> :
                    <>
                        <Row justify="space-between" align="middle">
                            <Col>
                                <Space>
                                    <Title style={{ color: "#244460" }} level={5}>Clusters</Title>
                                </Space>
                            </Col>
                            <Col>
                                {
                                    !!clusters.length ? <ClusterActions /> : null
                                }
                            </Col>
                        </Row>
                        <Card
                            type="inner"
                            title={
                                <Row gutter={24} align="middle" className="cluster-item-title">
                                    <Col xs={24} sm={8} md={6} lg={7}>
                                        Name
                                    </Col>
                                    <Col xs={24} sm={8} md={3} lg={3}>
                                        <Space>
                                            <span>
                                                Type
                                            </span>
                                            <Tooltip title={"Personal or Shared with other Microtica users"}>
                                                <InfoCircleTwoTone />
                                            </Tooltip>
                                        </Space>
                                    </Col>
                                    <Col xs={24} sm={8} md={5} lg={5}>
                                        <Space>
                                            <span>
                                                Access limitation
                                            </span>
                                            <Tooltip title={"Access only to a specific namespace or entire cluster"}>
                                                <InfoCircleTwoTone />
                                            </Tooltip>
                                        </Space>
                                    </Col>
                                    <Col xs={24} sm={8} md={2} lg={2}>
                                        Nodes
                                    </Col>
                                    <Col xs={24} sm={8} md={6} lg={5}>
                                        Usage
                                    </Col>
                                    <Col xs={24} sm={8} md={2} lg={2}>
                                        Actions
                                    </Col>
                                </Row>
                            }>

                            {
                                !clusters.length ?
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ marginBottom: 30 }}>
                                            <SearchOutlined style={{ fontSize: 60, verticalAlign: "middle", color: "#244460" }} />
                                            <Title level={5} style={{ color: "black" }}>It looks like you don't have any clusters in your environment</Title>
                                            <p style={{ color: "black" }}>Create or connect a Kubernetes cluster to deploy your applications</p>
                                        </div>
                                        <ClusterActions />
                                    </div>
                                    :
                                    clusters.map(cluster => (
                                        <Row gutter={24} style={{ marginBottom: 5 }} align="middle" key={cluster.name} className="cluster-item">
                                            <Col xs={24} sm={8} md={6} lg={7}>
                                                <>
                                                    <Space>
                                                        <img width={25} src={KubernetesLogo} style={{ verticalAlign: "bottom" }} alt="" />
                                                        <Space>
                                                            {cluster.name}
                                                            <Tooltip title={<Text copyable className="tooltip-copyable">{cluster.endpoint}</Text>}>
                                                                <GlobalOutlined className="primary-color" />
                                                            </Tooltip>
                                                        </Space>
                                                    </Space>
                                                </>
                                            </Col>
                                            <Col xs={24} sm={8} md={3} lg={3}>
                                                {capitalizeFirstLetter(cluster.type)}
                                            </Col>
                                            <Col xs={24} sm={8} md={5} lg={5}>
                                                <Tag color={cluster.associatedNamespace ? "blue" : "default"}>{cluster.associatedNamespace || "Full access"}</Tag>
                                            </Col>
                                            <Col xs={24} sm={8} md={2} lg={2}>
                                                {cluster.nodesStatus?.totalNodes} nodes
                                            </Col>
                                            <Col xs={24} sm={8} md={6} lg={5}>
                                                <Space>
                                                    <div style={{ width: 100, fontSize: "smaller" }}>
                                                        <span>CPU: {Math.round(cluster!.nodesStatus?.utilization.averageCpuUtilization! * 100)}%</span>
                                                        <Progress percent={cluster!.nodesStatus?.utilization.averageCpuUtilization! * 100} trailColor="lightgray" size="small" showInfo={false} />
                                                    </div>
                                                    <div style={{ width: 100, fontSize: "smaller" }}>
                                                        <span>Memory: {Math.round(cluster!.nodesStatus?.utilization.averageMemoryUtilization! * 100)}%</span>
                                                        <Progress percent={cluster!.nodesStatus?.utilization.averageMemoryUtilization! * 100} trailColor="lightgray" size="small" showInfo={false} />
                                                    </div>
                                                </Space>
                                            </Col>
                                            <Col xs={24} sm={8} md={2} lg={2}>
                                                <Dropdown
                                                    placement="bottomRight"
                                                    trigger={["click"]}
                                                    menu={{
                                                        items: [
                                                            {
                                                                key: "delete",
                                                                label: cluster.type === ListKubernetesWithDetailsResponseKubernetesesTypeEnum.Shared ?
                                                                    "Delete" :
                                                                    <Tooltip
                                                                        title={`This cluster was created by your "${cluster.name.replace(`${environment.id}-`, "")}" resource. You can delete this cluster by undeploying that resource.`}
                                                                    >
                                                                        Delete
                                                                    </Tooltip>,
                                                                danger: cluster.type === ListKubernetesWithDetailsResponseKubernetesesTypeEnum.Shared,
                                                                disabled: cluster.type !== ListKubernetesWithDetailsResponseKubernetesesTypeEnum.Shared,
                                                                onClick: () => showDeleteClusterConfirm(cluster.id, cluster.name)
                                                            }
                                                        ]
                                                    }}
                                                >
                                                    <Button icon={<MoreOutlined />} />
                                                </Dropdown>
                                            </Col>
                                        </Row>
                                    ))
                            }
                        </Card>
                    </>
            }
        </Space>
    )
}


export default StageClustersTable;